import { Injectable, inject } from '@angular/core';
import { Router, CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot, CanActivateFn } from '@angular/router';
import { AuthService } from '~/core/services/auth.service';
import { RoleService, UserRole } from '~/core/services/role.service';
import { firstValueFrom } from 'rxjs';

/**
 * Route guard that checks user role before allowing access
 *
 * Usage in routing:
 * {
 *   path: 'sme-dashboard',
 *   component: SmeComponent,
 *   canActivate: [RoleGuard],
 *   data: { requiredRoles: [UserRole.SME, UserRole.ADMIN] }
 * }
 */
@Injectable({
  providedIn: 'root'
})
export class RoleGuard implements CanActivate {

  constructor(
    private readonly authService: AuthService,
    private readonly roleService: RoleService,
    private readonly router: Router
  ) { }

  async canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Promise<boolean> {
    try {
      const isAuthenticated = await this.authService.ensureAuthenticated();
      if (!isAuthenticated) {
        return false;
      }

      // Get required roles from route config
      const requiredRoles: UserRole[] = route.data['requiredRoles'] || [];

      if (requiredRoles.length === 0) {
        // No specific roles required, allow access
        return true;
      }

      // Get user's current roles
      const userHasRole = await firstValueFrom(
        this.roleService.hasAnyRole(requiredRoles)
      );

      if (userHasRole) {
        return true;
      }

      // User doesn't have required role
      console.warn(
        `Access denied to ${state.url} - requires one of roles: ${requiredRoles.join(', ')}`
      );

      // Redirect to home or access denied page
      await this.router.navigate(['/unauthorized']);
      return false;

    } catch (error) {
      console.error('Error checking role:', error);
      return false;
    }
  }
}

/**
 * Redirects the root path to the role-appropriate landing page.
 * SME/ADMIN → /sme-dashboard, GM → /gm-dashboard, USER → /selection
 */
export const roleRedirectGuard: CanActivateFn = async () => {
  const authService = inject(AuthService);
  const roleService = inject(RoleService);
  const router = inject(Router);

  const isAuthenticated = await authService.ensureAuthenticated();
  if (!isAuthenticated) return false;

  const highestRole = await firstValueFrom(roleService.getHighestRole());

  switch (highestRole) {
    case UserRole.ADMIN:
      return router.createUrlTree(['/selection']);
    case UserRole.SME:
      return router.createUrlTree(['/sme-dashboard']);
    case UserRole.GM:
      return router.createUrlTree(['/gm-dashboard']);
    case UserRole.USER:
      return router.createUrlTree(['/selection']);
    default:
      return router.createUrlTree(['/unauthorized']);
  }
};
